import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, type Recommendation } from "../api";
import { Button, Card, Input, SectionHeading, Spinner, Tag } from "../components/ui";

export default function RecommendationsPage() {
  const [selected, setSelected] = useState<string[]>([]);
  const [custom, setCustom] = useState("");
  const [vibes, setVibes] = useState("");

  const themes = useQuery({
    queryKey: ["rec-themes"],
    queryFn: () => api.get<{ themes: string[] }>("/recommendations/themes"),
  });

  const generate = useMutation({
    mutationFn: () =>
      api.post<{ recommendations: Recommendation[] }>("/recommendations/generate", {
        themes: selected,
        vibes: vibes.trim() || null,
      }),
  });

  function toggle(t: string) {
    setSelected((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  }

  function addCustom() {
    const t = custom.trim().toLowerCase();
    if (!t) return;
    if (!selected.includes(t)) setSelected((s) => [...s, t]);
    setCustom("");
  }

  const recs = generate.data?.recommendations ?? [];

  return (
    <div className="flex flex-col gap-10">
      <SectionHeading
        eyebrow="What should we read next?"
        title="Recommendations"
        description="Pick a few themes or describe a mood. We'll pull together five books worth considering — and skip anything already in the library."
      />

      <Card accent>
        <div className="flex flex-col gap-6">
          <div>
            <label className="text-xs font-medium uppercase tracking-[0.12em] text-plum-soft block mb-3">
              Themes / topics
            </label>
            {selected.length > 0 ? (
              <div className="flex flex-wrap gap-2 mb-3">
                {selected.map((t) => (
                  <button
                    key={t}
                    onClick={() => toggle(t)}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose text-white text-xs font-medium hover:bg-rose-dark"
                  >
                    {t} <span>×</span>
                  </button>
                ))}
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              {(themes.data?.themes ?? [])
                .filter((t) => !selected.includes(t))
                .map((t) => (
                  <button
                    key={t}
                    onClick={() => toggle(t)}
                    className="inline-flex items-center px-3 py-1 rounded-full bg-cream-100 text-plum-soft text-xs hover:bg-blush-soft hover:text-rose-dark"
                  >
                    + {t}
                  </button>
                ))}
            </div>

            <div className="flex gap-2 mt-3">
              <input
                type="text"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustom();
                  }
                }}
                placeholder="Add your own theme"
                className="flex-1 px-3 py-2 rounded-lg bg-cream-50 border border-cream-300 text-sm text-plum placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose"
              />
              <button
                onClick={addCustom}
                className="px-3 py-2 text-xs uppercase tracking-[0.14em] text-plum-soft hover:text-rose"
              >
                Add
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-[0.12em] text-plum-soft block mb-1.5">
              Anything else?
            </label>
            <Input
              value={vibes}
              onChange={(e) => setVibes(e.target.value)}
              placeholder="e.g. nothing too long; we want a mood-shifter after Thrashers"
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={() => generate.mutate()} loading={generate.isPending}>
              {recs.length > 0 ? "Get another five" : "Find five books"}
            </Button>
          </div>
        </div>
      </Card>

      {generate.error ? (
        <p className="text-rose-dark text-sm text-center">{(generate.error as Error).message}</p>
      ) : null}

      {generate.isPending ? (
        <div className="flex flex-col items-center py-10 gap-4">
          <Spinner size={28} />
          <p className="text-plum-soft italic">Asking for five thoughtful suggestions…</p>
        </div>
      ) : null}

      {recs.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-5">
          {recs.map((r, i) => (
            <Card key={i} className="flex flex-col gap-3">
              <div>
                <h3 className="font-serif text-2xl text-plum leading-tight">{r.title}</h3>
                <p className="text-plum-soft italic mt-1">
                  by {r.author}
                  {r.yearPublished ? ` · ${r.yearPublished}` : ""}
                </p>
              </div>
              {r.vibeTags?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {r.vibeTags.map((v) => (
                    <Tag key={v}>{v}</Tag>
                  ))}
                </div>
              ) : null}
              <p className="text-plum leading-relaxed">{r.blurb}</p>
              <div className="mt-1 pt-3 border-t border-cream-200">
                <span className="text-[0.7rem] uppercase tracking-[0.14em] text-gold-dark">
                  Why for us
                </span>
                <p className="text-plum-soft text-sm leading-relaxed mt-1.5 italic">{r.whyForUs}</p>
              </div>
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
}
